import { pure } from '@core/helpers/pure';
import { TuiSelect, TuiChevron } from '@taiga-ui/kit';
import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  inject,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TuiDataListWrapper } from '@taiga-ui/kit';
import {
  TuiDialogContext,
  TuiError,
  TuiButton,
  TuiDropdown,
  TuiTextfield,
} from '@taiga-ui/core';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { Nullable } from '@core/helpers/nullable';
import { RequestStatusChangeReasonForm } from '../../types';
import { TranslatePipe } from '@ngx-translate/core';

enum FormFields {
  Reason = 'reason',
}
@Component({
  selector: 'x-request-form-status-cancel-initiator',
  imports: [
    TuiTextfield,
    TuiChevron,
    TuiSelect,
    TuiDataListWrapper,
    TuiError,
    TuiButton,
    ReactiveFormsModule,
    TranslatePipe,
    TuiDropdown,
  ],
  templateUrl: './request-form-status-cancel-reason.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestFormStatusCancelReasonComponent {
  private readonly context =
    inject<TuiDialogContext<Nullable<RequestStatusChangeReasonForm>>>(
      POLYMORPHEUS_CONTEXT,
    );
  private readonly document = inject(DOCUMENT);
  private promotedDropdown: HTMLElement | null = null;
  private dropdownObserver: MutationObserver | null = null;
  private dropdownObserverTimeoutId: ReturnType<typeof setTimeout> | null =
    null;

  readonly items = ['ЛК', 'ЛП', 'Поставщик'];
  readonly formGroup = new FormGroup({
    [FormFields.Reason]: new FormControl<Nullable<string>>(
      null,
      Validators.required,
    ),
  });
  readonly FormFields = FormFields;

  @pure
  get reasonField() {
    return this.formGroup.get(FormFields.Reason);
  }

  submit() {
    this.stopDropdownObserver();
    this.resetPromotedDropdownZIndex();
    this.context.completeWith(this.formGroup.getRawValue());
  }

  dismiss() {
    this.stopDropdownObserver();
    this.resetPromotedDropdownZIndex();
    this.context.completeWith(null);
  }

  promoteNestedDropdownZIndex(): void {
    this.stopDropdownObserver();

    const existingDropdowns = new Set(
      Array.from(this.document.querySelectorAll('tui-dropdown')),
    );

    const tryPromoteNewDropdown = () => {
      const dropdowns = Array.from(
        this.document.querySelectorAll('tui-dropdown'),
      );
      const newDropdown = dropdowns.find(
        dropdown => !existingDropdowns.has(dropdown),
      );

      if (!(newDropdown instanceof HTMLElement)) {
        return false;
      }

      if (this.promotedDropdown && this.promotedDropdown !== newDropdown) {
        this.promotedDropdown.style.removeProperty('z-index');
      }

      this.promotedDropdown = newDropdown;
      this.promotedDropdown.style.setProperty('z-index', '2');
      this.stopDropdownObserver();

      return true;
    };

    if (tryPromoteNewDropdown()) {
      return;
    }

    // Оказалось, что tui не позволяет сделать конкретный
    // инстанс dropdown идентифицируемым. Никак. Вообще.

    // Единственное, что можно сделать:
    // 1. переделывать всю систему смены статусов на странице заявки;
    // 2. вот такой костыль - во время показывания модалки отслеживать dropdown,
    // который спавнится в хосте, и выводить его наверх.
    this.dropdownObserver = new MutationObserver(() => {
      tryPromoteNewDropdown();
    });

    this.dropdownObserver.observe(this.document.body, {
      childList: true,
      subtree: true,
    });

    this.dropdownObserverTimeoutId = setTimeout(() => {
      this.stopDropdownObserver();
    }, 800);
  }

  private stopDropdownObserver(): void {
    this.dropdownObserver?.disconnect();
    this.dropdownObserver = null;

    if (this.dropdownObserverTimeoutId) {
      clearTimeout(this.dropdownObserverTimeoutId);
      this.dropdownObserverTimeoutId = null;
    }
  }

  private resetPromotedDropdownZIndex(): void {
    if (!this.promotedDropdown) {
      return;
    }

    this.promotedDropdown.style.removeProperty('z-index');
    this.promotedDropdown = null;
  }
}
